import { createAutocomplete } from "@algolia/autocomplete-core";
import type { AutocompleteApi, AutocompleteSource, AutocompleteState } from "@algolia/autocomplete-core";
import * as Sentry from "@sentry/browser";

import { renderOption } from "./option_element";
import type { Messages, OptionLookup, SearchableOption, SearchableSelectConfig, SearchableSelectElements } from "./types";
import { debouncePromise } from "src/debounce";

type Row = { option: SearchableOption; element: HTMLAnchorElement };
type State = AutocompleteState<SearchableOption>;
type Source = AutocompleteSource<SearchableOption>;
type SetContext = (context: Record<string, unknown>) => void;

const DEFAULT_MESSAGES: Messages = {
  typeToSearch: (minLength) => (minLength > 1 ? `Type at least ${minLength} characters to search` : "Type to search"),
  loading: "Loading…",
  noMatches: "No matches",
  error: "Couldn't load options",
};

const OPTIONS_SOURCE_ID = "options";

let instanceCounter = 0;

export class SearchableSelect {
  private readonly id: string;
  private readonly autocomplete: AutocompleteApi<SearchableOption>;
  private readonly sources: { blank: Source | null; options: Source };
  private readonly debounce: (sources: Source[]) => Promise<Source[]>;
  private readonly cancelDebounce: () => void;
  private readonly minLength: number;
  private readonly debounceMs: number;
  private readonly messages: Messages = DEFAULT_MESSAGES;
  private readonly menu: HTMLElement | null;
  private readonly alwaysUp: boolean;

  private state?: State;
  private rows: Row[] = [];
  private prompting = false;
  private destroyed = false;

  constructor(private readonly els: SearchableSelectElements, config: SearchableSelectConfig) {
    this.minLength = config.minLength ?? 0;
    this.debounceMs = config.debounceMs ?? 250;

    this.menu = els.root.querySelector(".dropdown-menu");
    this.alwaysUp = els.root.classList.contains("is-up");

    this.id = els.root.id || `searchable-select-${++instanceCounter}`;
    if (!els.list.id) els.list.id = `${this.id}-listbox`;
    els.search.setAttribute("aria-controls", els.list.id);

    [this.debounce, this.cancelDebounce] = debouncePromise<Source[]>(this.debounceMs);
    this.sources = { blank: this.blankSource(config.blankLabel ?? null), options: this.optionsSource(config.lookup) };

    this.autocomplete = createAutocomplete<SearchableOption>({
      id: this.id,
      openOnFocus: true,
      defaultActiveItemId: 0,
      shouldPanelOpen: () => this.isOpen,
      getSources: ({ query }) => this.getSources(query),
      onStateChange: ({ prevState, state }) => this.onStateChange(prevState, state),
    });

    els.trigger.addEventListener("click", this.onTriggerClick);
    els.trigger.addEventListener("keydown", this.onTriggerKeydown);
    els.search.addEventListener("input", this.onSearchInput);
    els.search.addEventListener("keydown", this.onSearchKeydown);
    els.search.addEventListener("change", this.onSearchChange);
    els.list.addEventListener("mousedown", this.onListMousedown);
    els.list.addEventListener("click", this.onListClick);
    els.list.addEventListener("mousemove", this.onListMousemove);
  }

  get value(): string {
    return this.els.hidden.value;
  }

  get isOpen(): boolean {
    return this.els.root.classList.contains("is-active");
  }

  open(): void {
    if (this.destroyed || this.isOpen || this.els.trigger.disabled) return;

    this.els.root.classList.add("is-active");
    this.setExpanded(true);
    document.addEventListener("pointerdown", this.onDocumentPointerDown);
    window.addEventListener("blur", this.onWindowBlur);
    this.els.search.value = "";
    this.autocomplete.setQuery("");
    this.autocomplete.setIsOpen(true);
    void this.autocomplete.refresh();
    this.chooseDirection();
    this.els.search.focus();
  }

  close(opts: { restoreFocus?: boolean } = {}): void {
    if (!this.isOpen) return;

    this.cancelDebounce();
    this.els.root.classList.remove("is-active");
    this.setExpanded(false);
    document.removeEventListener("pointerdown", this.onDocumentPointerDown);
    window.removeEventListener("blur", this.onWindowBlur);
    this.autocomplete.setIsOpen(false);
    this.rows.forEach((row) => row.element.classList.remove("is-active"));
    this.els.search.removeAttribute("aria-activedescendant");
    if (opts.restoreFocus) this.els.trigger.focus();
  }

  toggle(): void {
    if (this.isOpen) this.close({ restoreFocus: true });
    else this.open();
  }

  setQuery(query: string): void {
    this.els.search.value = query;
    this.autocomplete.setQuery(query);
    void this.autocomplete.refresh();
  }

  select(option: SearchableOption): void {
    if (option.value !== this.els.hidden.value) {
      this.writeValue(option);
      this.els.hidden.dispatchEvent(new Event("change", { bubbles: true }));
      this.els.root.dispatchEvent(new CustomEvent("searchable-select:change", {
        bubbles: true,
        detail: { value: option.value, label: option.label },
      }));
    }

    this.close({ restoreFocus: true });
  }

  destroy(): void {
    if (this.destroyed) return;

    this.close();
    this.destroyed = true;
    const { trigger, search, list } = this.els;
    trigger.removeEventListener("click", this.onTriggerClick);
    trigger.removeEventListener("keydown", this.onTriggerKeydown);
    search.removeEventListener("input", this.onSearchInput);
    search.removeEventListener("keydown", this.onSearchKeydown);
    search.removeEventListener("change", this.onSearchChange);
    list.removeEventListener("mousedown", this.onListMousedown);
    list.removeEventListener("click", this.onListClick);
    list.removeEventListener("mousemove", this.onListMousemove);
  }

  private blankSource(label: string | null): Source | null {
    if (label === null) return null;

    const blank: SearchableOption = { value: "", label };
    return { sourceId: "blank", getItems: () => [blank] };
  }

  private optionsSource(lookup: OptionLookup): Source {
    return {
      sourceId: OPTIONS_SOURCE_ID,
      getItems: async ({ query, setContext }) => {
        setContext({ failedQuery: null });
        try {
          return await lookup(query);
        } catch (error) {
          this.fail(query, error, setContext);
          return [];
        }
      },
    };
  }

  private getSources(query: string): Source[] | Promise<Source[]> {
    const belowMinimum = query.length < this.minLength;
    const sources = [this.sources.blank, belowMinimum ? null : this.sources.options].filter((source) => source !== null);

    return belowMinimum || query === "" || this.debounceMs === 0 ? sources : this.debounce(sources);
  }

  private fail(query: string, error: unknown, setContext: SetContext): void {
    Sentry.captureException(error, { extra: { queryLength: query.length } });
    setContext({ failedQuery: query });
  }

  private onStateChange(prevState: State, state: State): void {
    this.state = state;
    if (this.destroyed || !this.isOpen) return;

    const fresh = prevState.collections !== state.collections || this.rows.length === 0;
    if (fresh) {
      this.renderRows(state);
      const selectedIndex = this.rows.findIndex((row) => row.option.value === this.els.hidden.value);
      if (selectedIndex >= 0 && selectedIndex !== state.activeItemId) {
        this.highlightAt(selectedIndex);
        return;
      }
    }

    this.renderHighlight(state, { scroll: fresh });
    this.renderStatus(state);
  }

  private renderRows(state: State): void {
    const current = this.els.hidden.value;
    const fragment = document.createDocumentFragment();

    this.rows = state.collections.flatMap(({ source, items }) => items.map((option) => {
      const element = renderOption(option, this.autocomplete.getItemProps({ item: option, source }).id);
      if (source.sourceId === "blank") element.classList.add("searchable-select-blank");
      if (option.value === current) element.setAttribute("aria-selected", "true");
      fragment.append(element);
      return { option, element };
    }));
    this.els.list.replaceChildren(fragment);
  }

  private renderHighlight(state: State, opts: { scroll: boolean }): void {
    this.rows.forEach((row, index) => row.element.classList.toggle("is-active", index === state.activeItemId));

    const active = state.activeItemId === null ? undefined : this.rows[state.activeItemId];
    if (active) {
      this.els.search.setAttribute("aria-activedescendant", active.element.id);
      if (opts.scroll && typeof active.element.scrollIntoView === "function") active.element.scrollIntoView({ block: "nearest" });
    } else {
      this.els.search.removeAttribute("aria-activedescendant");
    }
  }

  private renderStatus(state: State): void {
    const wasPrompting = this.prompting;
    this.prompting = state.query.length < this.minLength;
    if (this.prompting) return this.setStatus(this.messages.typeToSearch(this.minLength));
    if (state.status === "stalled") return this.setStatus(this.messages.loading);

    if (state.status === "loading") {
      if (wasPrompting) this.hideStatus();
      return;
    }
    if (state.context.failedQuery === state.query) return this.setStatus(this.messages.error);

    const results = state.collections.find((collection) => collection.source.sourceId === OPTIONS_SOURCE_ID);
    if (results && results.items.length === 0) this.setStatus(this.messages.noMatches);
    else this.hideStatus();
  }

  private chooseDirection(): void {
    if (this.alwaysUp || !this.menu) return;

    const listMaxHeight = parseFloat(getComputedStyle(this.els.list).maxHeight) || 0;
    const needed = Math.max(this.menu.offsetHeight, this.menu.offsetHeight - this.els.list.offsetHeight + listMaxHeight);
    const rect = this.els.trigger.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;

    this.els.root.classList.toggle("is-up", below < needed && above > below);
  }

  private highlightAt(index: number): void {
    this.autocomplete.setActiveItemId(index);
    this.rows[index]?.element.scrollIntoView?.({ block: "nearest" });
  }

  private writeValue(option: SearchableOption): void {
    this.els.hidden.value = option.value;
    this.els.label.textContent = option.label;
  }

  private setExpanded(expanded: boolean): void {
    const value = expanded ? "true" : "false";
    this.els.trigger.setAttribute("aria-expanded", value);
    this.els.search.setAttribute("aria-expanded", value);
  }

  private setStatus(text: string): void {
    this.els.status.textContent = text;
    this.els.status.hidden = false;
  }

  private hideStatus(): void {
    this.els.status.textContent = "";
    this.els.status.hidden = true;
  }

  private rowIndexFor(target: EventTarget | null): number {
    const element = (target as HTMLElement | null)?.closest("[role=option]");
    return element ? this.rows.findIndex((row) => row.element === element) : -1;
  }

  private readonly onTriggerClick = (): void => {
    this.toggle();
  };

  private readonly onTriggerKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      this.els.trigger.closest("form")?.requestSubmit();
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      this.open();
    }
  };

  private readonly onSearchInput = (): void => {
    this.setQuery(this.els.search.value);
  };

  private readonly onSearchChange = (event: Event): void => {
    event.stopPropagation();
  };

  private readonly onSearchKeydown = (event: KeyboardEvent): void => {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        this.autocomplete.getInputProps({ inputElement: this.els.search }).onKeyDown(event);
        break;
      case "Enter": {
        event.preventDefault();
        const activeItemId = this.state?.activeItemId;
        const row = activeItemId === null || activeItemId === undefined ? undefined : this.rows[activeItemId];
        if (row) this.select(row.option);
        break;
      }
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        this.close({ restoreFocus: true });
        break;
      case "Tab":
        this.close();
        break;
    }
  };

  private readonly onListMousedown = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private readonly onListClick = (event: MouseEvent): void => {
    const index = this.rowIndexFor(event.target);
    if (index >= 0) this.select(this.rows[index].option);
  };

  private readonly onListMousemove = (event: MouseEvent): void => {
    const index = this.rowIndexFor(event.target);
    if (index >= 0 && index !== this.state?.activeItemId) this.autocomplete.setActiveItemId(index);
  };

  private readonly onDocumentPointerDown = (event: PointerEvent): void => {
    if (!this.els.root.contains(event.target as Node)) this.close();
  };

  private readonly onWindowBlur = (): void => {
    this.close();
  };
}
